import type { ClientOverview, ClientUnit, Currency, Unit } from '../../types/api';
import { convertUSDToCurrency } from './currencyConversion';

export type CompositionDimension = 'location' | 'currency' | 'operatingModel';

export interface CompositionSlice {
  name: string;
  country?: string;
  value: number;
  percentage: number;
}

function normalizeKey(value?: string | null): string {
  return value?.trim().toLowerCase() || 'unknown';
}

function toDisplayLabel(value?: string | null, fallback = 'Unknown'): string {
  const trimmed = value?.trim();
  return trimmed || fallback;
}

function getAssetValue(
  overview: ClientOverview,
  targetCurrencyCode: string,
  currencies: Currency[],
  needsConversion: boolean,
): number {
  const usdValue = overview.total_assets_usd || 0;
  if (!needsConversion) return usdValue;
  return convertUSDToCurrency(usdValue, targetCurrencyCode, currencies);
}

function getDimensionBucket(
  dimension: CompositionDimension,
  unit: Unit | undefined,
  clientUnit: ClientUnit | undefined,
  overview: ClientOverview,
): { key: string; name: string; country?: string } {
  switch (dimension) {
    case 'location': {
      const city = unit?.city?.trim();
      const country = unit?.country?.trim() || clientUnit?.country?.trim();
      const name = city || country || 'Unknown location';
      const key = normalizeKey(`${city || ''}|${country || ''}`) || 'unknown';
      return {
        key,
        name,
        country: city && country ? country : undefined,
      };
    }
    case 'currency': {
      const label = toDisplayLabel(unit?.currency || overview.unit_currency, 'Unknown currency');
      return { key: normalizeKey(label), name: label };
    }
    case 'operatingModel': {
      const label = toDisplayLabel(unit?.operating_model || clientUnit?.operating_model, 'Unknown model');
      return { key: normalizeKey(label), name: label };
    }
    default:
      return { key: 'unknown', name: 'Unknown' };
  }
}

export function generatePortfolioCompositionData(
  clientOverviewData: ClientOverview[],
  units: Unit[],
  clientUnits: ClientUnit[],
  dimension: CompositionDimension,
  targetCurrencyCode = 'USD',
  currencies: Currency[] = [],
): CompositionSlice[] {
  if (!clientOverviewData.length) return [];

  const unitMap = new Map<number, Unit>();
  units.forEach((unit) => {
    if (unit.unit_id_no) unitMap.set(unit.unit_id_no, unit);
  });

  const clientUnitMap = new Map<number, ClientUnit>();
  clientUnits.forEach((unit) => {
    if (unit.unit_id_no) clientUnitMap.set(unit.unit_id_no, unit);
  });

  const needsConversion = targetCurrencyCode !== 'USD' && currencies.length > 0;
  const buckets = new Map<string, { label: string; country?: string; value: number }>();

  clientOverviewData.forEach((overview) => {
    const assetValue = getAssetValue(overview, targetCurrencyCode, currencies, needsConversion);
    if (assetValue <= 0) return;

    const unit = unitMap.get(overview.unit_id_no);
    const clientUnit = clientUnitMap.get(overview.unit_id_no);
    const bucket = getDimensionBucket(dimension, unit, clientUnit, overview);

    const existing = buckets.get(bucket.key);
    if (existing) {
      existing.value += assetValue;
      return;
    }

    buckets.set(bucket.key, {
      label: bucket.name,
      country: bucket.country,
      value: assetValue,
    });
  });

  const slices = Array.from(buckets.values())
    .map(({ label, country, value }) => ({ name: label, country, value, percentage: 0 }))
    .sort((a, b) => b.value - a.value);

  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  if (total <= 0) return slices;

  return slices.map((slice) => ({
    ...slice,
    percentage: (slice.value / total) * 100,
  }));
}

export function generateAllPortfolioCompositionData(
  clientOverviewData: ClientOverview[],
  units: Unit[],
  clientUnits: ClientUnit[],
  targetCurrencyCode = 'USD',
  currencies: Currency[] = [],
): Record<CompositionDimension, CompositionSlice[]> {
  return {
    location: generatePortfolioCompositionData(
      clientOverviewData,
      units,
      clientUnits,
      'location',
      targetCurrencyCode,
      currencies,
    ),
    currency: generatePortfolioCompositionData(
      clientOverviewData,
      units,
      clientUnits,
      'currency',
      targetCurrencyCode,
      currencies,
    ),
    operatingModel: generatePortfolioCompositionData(
      clientOverviewData,
      units,
      clientUnits,
      'operatingModel',
      targetCurrencyCode,
      currencies,
    ),
  };
}
